import { useState, useEffect } from 'react';
import { FormField } from '../shared/form/FormField';
import { FormSelect } from '../shared/form/FormSelect';
import { FormTextArea } from '../shared/form/FormTextArea';
import type { FieldDefinition, FieldType, StringFormat } from '../../utils/schemaBuilder';
import { createEmptyField, isValidFieldName } from '../../utils/schemaBuilder';

interface FieldEditorProps {
  field: FieldDefinition | null;
  existingNames: string[];
  onSave: (field: FieldDefinition) => void;
  onCancel: () => void;
}

const TYPE_OPTIONS = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Boolean' },
];

const FORMAT_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'email', label: 'Email' },
  { value: 'uri', label: 'URL' },
  { value: 'date', label: 'Date' },
  { value: 'date-time', label: 'Date/Time' },
];

export function FieldEditor({ field, existingNames, onSave, onCancel }: FieldEditorProps) {
  const isEditing = field !== null;
  const [editedField, setEditedField] = useState<FieldDefinition>(() =>
    field || createEmptyField()
  );
  const [useEnum, setUseEnum] = useState(() =>
    field?.enumValues && field.enumValues.length > 0
  );
  const [enumInput, setEnumInput] = useState(() =>
    field?.enumValues?.join(', ') || ''
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (field) {
      setEditedField(field);
      setUseEnum(field.enumValues && field.enumValues.length > 0);
      setEnumInput(field.enumValues?.join(', ') || '');
    }
  }, [field]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!editedField.name.trim()) {
      newErrors.name = 'Field name is required';
    } else if (!isValidFieldName(editedField.name)) {
      newErrors.name = 'Field name must start with a letter or underscore, and contain only letters, numbers, and underscores';
    } else if (existingNames.includes(editedField.name) && (!isEditing || field?.name !== editedField.name)) {
      newErrors.name = 'A field with this name already exists';
    }

    if (useEnum && editedField.type === 'string') {
      const values = enumInput.split(',').map(v => v.trim()).filter(v => v);
      if (values.length === 0) {
        newErrors.enum = 'At least one option is required for dropdown';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const fieldToSave: FieldDefinition = {
      ...editedField,
      name: editedField.name.trim(),
    };

    // Handle enum values
    if (useEnum && editedField.type === 'string') {
      fieldToSave.enumValues = enumInput.split(',').map(v => v.trim()).filter(v => v);
      fieldToSave.format = undefined; // Can't have both enum and format
    } else {
      fieldToSave.enumValues = undefined;
    }

    // Clear irrelevant fields based on type
    if (editedField.type !== 'string') {
      fieldToSave.format = undefined;
      fieldToSave.enumValues = undefined;
      fieldToSave.minLength = undefined;
      fieldToSave.maxLength = undefined;
    }

    if (editedField.type !== 'number' && editedField.type !== 'integer') {
      fieldToSave.minimum = undefined;
      fieldToSave.maximum = undefined;
    }

    onSave(fieldToSave);
  };

  const updateField = (updates: Partial<FieldDefinition>) => {
    setEditedField(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
          onClick={onCancel}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {isEditing ? 'Edit Field' : 'Add Field'}
          </h3>

          <div className="space-y-4">
            {/* Field Name */}
            <FormField
              label="Field Name"
              name="fieldName"
              value={editedField.name}
              onChange={(e) => updateField({ name: e.target.value })}
              error={errors.name}
              required
              placeholder="e.g., message, userId"
            />

            {/* Field Type */}
            <FormSelect
              label="Type"
              name="fieldType"
              value={editedField.type}
              onChange={(e) => {
                const newType = e.target.value as FieldType;
                updateField({ type: newType });
                if (newType !== 'string') {
                  setUseEnum(false);
                }
              }}
              options={TYPE_OPTIONS}
              required
            />

            {/* Required Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="fieldRequired"
                checked={editedField.required}
                onChange={(e) => updateField({ required: e.target.checked })}
                className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="fieldRequired" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Required field
              </label>
            </div>

            {/* Description */}
            <FormTextArea
              label="Description"
              name="fieldDescription"
              value={editedField.description || ''}
              onChange={(e) => updateField({ description: e.target.value })}
              placeholder="Describe what this field is for"
              rows={2}
            />

            {/* String-specific options */}
            {editedField.type === 'string' && (
              <>
                {/* Dropdown toggle */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useDropdown"
                    checked={useEnum}
                    onChange={(e) => {
                      setUseEnum(e.target.checked);
                      if (e.target.checked) {
                        updateField({ format: undefined });
                      }
                    }}
                    className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="useDropdown" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Use dropdown (predefined options)
                  </label>
                </div>

                {useEnum ? (
                  /* Enum values */
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Options (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={enumInput}
                      onChange={(e) => setEnumInput(e.target.value)}
                      placeholder="e.g., en, fr, es"
                      className={`block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm
                        ${errors.enum
                          ? 'border-red-300 dark:border-red-600'
                          : 'border-gray-300 dark:border-gray-600'
                        }
                        bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                        focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
                      `}
                    />
                    {errors.enum && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.enum}</p>
                    )}
                  </div>
                ) : (
                  /* Format option */
                  <FormSelect
                    label="Format"
                    name="fieldFormat"
                    value={editedField.format || ''}
                    onChange={(e) => updateField({ format: (e.target.value || undefined) as StringFormat })}
                    options={FORMAT_OPTIONS}
                  />
                )}

                {/* String length constraints */}
                {!useEnum && (
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      label="Min Length"
                      name="minLength"
                      type="number"
                      value={editedField.minLength ?? ''}
                      onChange={(e) => updateField({
                        minLength: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      placeholder="0"
                    />
                    <FormField
                      label="Max Length"
                      name="maxLength"
                      type="number"
                      value={editedField.maxLength ?? ''}
                      onChange={(e) => updateField({
                        maxLength: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      placeholder="No limit"
                    />
                  </div>
                )}
              </>
            )}

            {/* Number-specific options */}
            {(editedField.type === 'number' || editedField.type === 'integer') && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Minimum"
                  name="minimum"
                  type="number"
                  value={editedField.minimum ?? ''}
                  onChange={(e) => updateField({
                    minimum: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  placeholder="No min"
                />
                <FormField
                  label="Maximum"
                  name="maximum"
                  type="number"
                  value={editedField.maximum ?? ''}
                  onChange={(e) => updateField({
                    maximum: e.target.value ? parseFloat(e.target.value) : undefined
                  })}
                  placeholder="No max"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {isEditing ? 'Save Changes' : 'Add Field'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
